import { BonusStage, LootboxStage } from 'api/common';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import { useContext, useMemo } from 'react';
import { EMissionBonusGroupKey, MISSION_BONUS_GROUP_KEYS } from '../helpers';
import { PRIZE_DONE_STATUSES } from './useMissions';

export const useMissionPrize = () => {
  const { freespins, bonuses, lootboxes } = useContext(RealTimeServiceContext);

  const prize = useMemo(() => {
    const sortByGroupKeyPriority = <T extends { groupKey?: string }>(
      items: T[]
    ) =>
      [...items].sort((a, b) => {
        const indexA = MISSION_BONUS_GROUP_KEYS.indexOf(
          a.groupKey as EMissionBonusGroupKey
        );
        const indexB = MISSION_BONUS_GROUP_KEYS.indexOf(
          b.groupKey as EMissionBonusGroupKey
        );
        return indexA - indexB;
      });

    const sortedBonuses = sortByGroupKeyPriority([...freespins, ...bonuses]);
    const sortedLootboxes = sortByGroupKeyPriority(lootboxes);

    const findByGroupKey = <T extends { groupKey?: string }>(
      items: T[],
      groupKey: EMissionBonusGroupKey
    ) => items.find((item) => item.groupKey === groupKey);

    const lootboxL5 = findByGroupKey(sortedLootboxes, EMissionBonusGroupKey.L5);
    const bonusesL4 = sortedBonuses.filter(
      (item) => item.groupKey === EMissionBonusGroupKey.L4
    );
    const lootboxL3 = findByGroupKey(sortedLootboxes, EMissionBonusGroupKey.L3);
    const bonusL2 = findByGroupKey(sortedBonuses, EMissionBonusGroupKey.L2);
    const bonusL1 = findByGroupKey(sortedBonuses, EMissionBonusGroupKey.L1);
    const bonusL0 = findByGroupKey(sortedBonuses, EMissionBonusGroupKey.L0);

    const isLootboxIssued = (lootbox: typeof lootboxL5) =>
      lootbox && lootbox.stage === LootboxStage.Issued;

    const isBonusIssued = (bonus: typeof bonusL0) =>
      bonus && bonus.stage === BonusStage.Issued;

    if (isLootboxIssued(lootboxL5)) return lootboxL5;

    if (
      bonusesL4?.filter((b) => b.stage === BonusStage.Issued).length &&
      !bonusesL4?.filter((b) => PRIZE_DONE_STATUSES.includes(b.stage)).length
    )
      return bonusesL4[0];
    if (isLootboxIssued(lootboxL3)) return lootboxL3;
    if (isBonusIssued(bonusL2)) return bonusL2;
    if (isBonusIssued(bonusL1)) return bonusL1;
    if (isBonusIssued(bonusL0)) return bonusL0;

    return null;
  }, [freespins, bonuses, lootboxes]);

  return prize;
};
